import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MEITRE_NEUTRAL_VARS } from '../meitreNeutralTheme';
import { LegalLanguageSelector } from './LegalLanguageSelector';
import { LEGAL_DOCS, legalPath, type LegalDocKey, type LegalLang } from '../legalDocs';
import { useLegalContent } from '../useLegalContent';

interface Props {
  doc: LegalDocKey;
  activeLang: LegalLang;
  children: ReactNode;
}

/**
 * Neutral, tenant-agnostic chrome for the legal pages. The inline
 * MEITRE_NEUTRAL_VARS force the default Meitre palette even on a themed
 * restaurant host. Header = Meitre wordmark + language switcher; footer =
 * cross-links to the other legal docs.
 */
export const LegalLayout = ({ doc, activeLang, children }: Props) => {
  const { data } = useLegalContent(activeLang);
  const docTitles = data?.docTitles;

  return (
    <div
      style={MEITRE_NEUTRAL_VARS}
      className="min-h-screen bg-background text-foreground"
    >
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
        <header className="mb-8 flex items-center justify-between gap-4">
          <Link
            to={legalPath('privacy-policy', activeLang)}
            className="font-display text-2xl text-foreground"
          >
            Meitre
          </Link>
          <LegalLanguageSelector doc={doc} activeLang={activeLang} />
        </header>

        <main className="rounded-2xl border border-border bg-card text-card-foreground px-6 py-8 shadow-sm sm:px-10 sm:py-12">
          {children}
        </main>

        <footer className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
          {LEGAL_DOCS.map((d) => (
            <Link
              key={d}
              to={legalPath(d, activeLang)}
              className={
                d === doc ? 'text-brand' : 'hover:text-foreground transition-colors'
              }
            >
              {docTitles?.[d] ?? ''}
            </Link>
          ))}
        </footer>
      </div>
    </div>
  );
};

export default LegalLayout;
